import React, { useEffect, useState } from "react";
import {
  Modal,
  View,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  Image,
  Keyboard,
} from "react-native";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { toast } from "react-toastify";
import { ShowLoader } from "../../../../common/ShowLoader";
import { AppImages } from "../../../../utility/AppImages";
import ConnectUserList from "../../../../Components/connect-user-common-components/ConnectUserList";
import CreateGroupButton from "../../../../Components/connect-user-common-components/CreateGroupButton";
import ConnectUserSearchBar from "../../../../Components/connect-user-common-components/ConnectUserSearchBar";
import { useChatApi } from "../../../../utility/hooks/useChatApi";
import { searchUsers } from "../../../../services/eventsAPI";
import CommonTextInputWeb from "../../../../common/CommonTextInputWeb";
import { useChat } from "../../../../context/ChatContext";

type ContactModalProps = {
  isContactVisible: boolean;
  onContactClose: () => void;
};

const DEBOUNCE_DELAY = 150;

const ContactModal: React.FC<ContactModalProps> = (props) => {
  const { isContactVisible, onContactClose = () => {} } = props;
  const { userData } = useChat();
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [allUsers, setAllUsers] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [search, setSearch] = useState(""); // Search text input state
  const [searchData, setSearchData] = useState([]); // Filtered search result
  const [arrListData, setArrListData] = useState([]); // Grouped and sorted data
  const [isCreateGroupShow, setIsCreateGroupShow] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]); // Selected users state
  const [debouncedSearch, setDebouncedSearch] = useState(""); // Debounced search state
  const [isSearchPerformed, setIsSearchPerformed] = useState(false); // Track if search is performed

  const { chatCreateAPI } = useChatApi();

  // Debounce the search input to avoid calling the API on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search); // Set the debounced search text
    }, DEBOUNCE_DELAY);

    // Clear timeout if the search input changes before the delay is over
    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  // Fetch users based on the debounced search input
  useEffect(() => {
    if (debouncedSearch.length > 0) {
      setIsSearchPerformed(true); // Search has been performed
      callSearchAPI({
        bodyParams: {
          query: debouncedSearch,
          currentUsername: userData.username,
        },
        responseSuccess: (response) => {
          if (response) {
            const users = response.results.map((item, index) => ({
              id: index,
              userID: item.userID,
              profilePicture: item.profilePicture,
              isSelected: 0,
            }));
            setAllUsers(users);
            setSearchData(users);
          }
        },
      });
    } else {
      setSearchData(allUsers);
    }
  }, [debouncedSearch]);

  // Maintain selected users when search results change
  useEffect(() => {
    let grouped = searchData.reduce((acc, obj) => {
      let firstChar = obj.userID.charAt(0).toUpperCase();
      if (!acc[firstChar]) {
        acc[firstChar] = [];
      }
      // Check if the user was previously selected
      obj.isSelected = selectedUsers.some((u) => u.userID === obj.userID)
        ? 1
        : 0;
      acc[firstChar].push(obj);
      return acc;
    }, {});

    let result = Object.keys(grouped).map((key) => ({
      firstChar: key,
      users: grouped[key],
    }));

    const sortedData = result.sort((a, b) => {
      if (a.firstChar < b.firstChar) return -1;
      if (a.firstChar > b.firstChar) return 1;
      return 0;
    });

    setArrListData(sortedData);
  }, [searchData, selectedUsers]);

  const callSearchAPI = async ({ bodyParams, responseSuccess }) => {
    setShowLoader(true);
    try {
      const data = await searchUsers(bodyParams);
      setShowLoader(false);

      if (data.statusCode === 200) {
        responseSuccess(data.body);
      } else {
        toast.error(data.body.error || "An error occurred");
      }
    } catch (error) {
      setShowLoader(false);
      toast.error(error.message);
    }
  };

  function updateArrSelectionValue(index: number, subIndex: number) {
    let arr = [...arrListData];
    const selectedUser = arr[index].users[subIndex];

    // Toggle selection state
    selectedUser.isSelected = selectedUser.isSelected === 1 ? 0 : 1;

    // Update selected users array
    let updatedSelectedUsers = selectedUser.isSelected
      ? [...selectedUsers, selectedUser]
      : selectedUsers.filter((u) => u.userID !== selectedUser.userID);

    setSelectedUsers(updatedSelectedUsers);

    // Show or hide the create group button based on the selection
    setIsCreateGroupShow(updatedSelectedUsers.length > 0);

    setArrListData(arr);
  }

  const onCreateGroupChat = () => {
    const selectedUserIDs = selectedUsers.map((user) => user.userID);
    chatCreateAPI(selectedUserIDs, userData.accessToken, "group");
  };

  return (
    <Modal transparent={true} visible={isContactVisible}>
      <View style={styles.container}>
        <View
          style={
            deviceSize === DEVICE_SIZES.XS
              ? styles.mobileContainer
              : styles.container
          }
        >
          <ScrollView
            contentContainerStyle={styles.mainView}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.header}>
              <Text style={[AppFontStyle.BOLD_ANYBODY_20, styles.headerText]}>
                Add to the group
              </Text>
              {isCreateGroupShow && (
                <CreateGroupButton
                  arrListData={arrListData}
                  onCreateGroupChat={onCreateGroupChat}
                  onContactClose={onContactClose}
                />
              )}
              <TouchableOpacity
                onPress={onContactClose}
                style={styles.closeButton}
              >
                <Image
                  source={AppImages.icCancel}
                  style={styles.closeIcon}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>
            {/* <ConnectUserSearchBar onChangeText={setSearch} /> */}
            <View
              style={{
                borderColor: AppColors.white_D6DDEB,
                borderWidth: 1,
                borderRadius: 10,
                marginTop: 15,
                marginRight: 5,
                width: 246,
                height: 32,
                justifyContent: "center",
              }}
            >
              <CommonTextInputWeb
                placeholder="Search Name"
                mainStyle={[styles.textInputStyle]}
                inputStyle={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { paddingVertical: 0, paddingHorizontal: 0 },
                ]}
                keyboardType="default"
                value={search}
                onChangeText={setSearch}
                prefixIconVisibility={true}
                onSubmitEditing={() => {
                  Keyboard.dismiss();
                }}
                autoCapitalize="none"
                isPassword={false}
                prefixIcon={AppImages.icSearchGray}
                iconStyle={{ width: 24, height: 24 }}
              />
            </View>
            <View style={styles.listContainer}>
              {arrListData.length === 0 && !isSearchPerformed ? ( // Show initial message before search
                <Text style={styles.noUserText}>
                  Search User to Create Group Chat
                </Text>
              ) : arrListData.length === 0 && isSearchPerformed ? ( // Show no user found message after search
                <Text style={styles.noUserText}>No such user found</Text>
              ) : (
                <ConnectUserList
                  data={arrListData}
                  onSelect={updateArrSelectionValue}
                />
              )}
            </View>
          </ScrollView>
        </View>
        {showLoader && <ShowLoader showIndicator={showLoader} />}
      </View>
    </Modal>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
      justifyContent: "center",
      alignItems: "center",
    },
    mobileContainer: {
      width: "85%",
      marginTop: 20,
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
    },
    mainView: {
      backgroundColor: AppColors.black_121212,
      borderColor: AppColors.grayColor_282828,
      borderRadius: 10,
      borderWidth: 1,
      padding: 20,
      width: 370,
      height: "94%",
      paddingHorizontal: 15,
      marginHorizontal: 30,
      marginVertical: "5%",
      alignSelf: "center",
    },
    header: {
      flexDirection: "row",
      paddingTop: 20,
      columnGap: 50,
    },
    headerText: {
      paddingBottom: 5,
      alignSelf: "center",
      flex: 1,
    },
    closeButton: {
      borderWidth: 1.8,
      borderColor: AppColors.grayColor_393939,
      borderRadius: 10,
      height: 30,
      width: 30,
    },
    closeIcon: {
      height: 12,
      width: 12,
      alignSelf: "center",
      marginVertical: 7,
    },
    listContainer: {
      flex: 1,
      paddingVertical: 20,
      paddingHorizontal: 10,
    },
    noUserText: {
      color: AppColors.white,
      fontSize: 16,
      textAlign: "center",
      marginTop: 20,
    },
    textInputStyle: {
      borderWidth: 0,
      paddingHorizontal: 10,
      // paddingVertical: 10,
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      textInputStyle: {
        width: "100%",
        marginRight: 0,
      },
    },
    [DEVICE_SIZES.XS]: {
      mainView: {
        width: "100%",
        padding: 12,
        marginHorizontal: 0,
        marginVertical: 0,
      },
      listContainer: {
        paddingVertical: 10,
        paddingHorizontal: 5,
      },
      textInputStyle: {
        // width: 200,
        marginRight: 0,
      },
    },
  }
);

export default ContactModal;
